import React, { useState } from "react";
import styles from "./settingsChangePassword.module.css";
import axios from "axios";
import { validatePassword } from "../../utilities/utils";

const apiUrl = process.env.REACT_APP_URL;

const SettingsChangePassword = ({ currUsername }) => {
  /* This component allows the user to change the account password. The user should enter the current password, and then choose a new password, and enter it twice. The new password should follow the website password restrictions. If the inputs are valid, the new password will be updated in the database, and a confirmation message will be shown. */

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isCurrentPasswordValid, setIsCurrentPasswordValid] = useState(null); // null means no validation yet
  const [isPasswordMatching, setIsPasswordMatching] = useState(true); // Initially true to avoid showing error immediately
  const [successfullPasswordChange, setSuccessfullPasswordChange] =
    useState(false);
  const [newPasswordError, setNewPasswordError] = useState(""); // State variable to manage the password error message
  const [isNewPasswordDisabled, setIsNewPasswordDisabled] = useState(false); // State to track whether the new password field should be disabled
  const [isNewPasswordSameAsOld, setIsNewPasswordSameAsOld] = useState(false);
  const [isConfirmationDisabled, setIsConfirmationDisabled] = useState(false);
  const [allFieldsValid, setAllFieldsValid] = useState(true);

  // Function to validate the current password
  const validateCurrentPassword = async () => {
    if (currentPassword.trim() === "") return;

    try {
      const res = await axios.post(
        `${apiUrl}/auth/previous_password_check/${currUsername}`,
        { password: currentPassword },
        {
          withCredentials: true,
        }
      );
      const isValid = res.status === 200 && res.data.success;
      setIsCurrentPasswordValid(isValid);
      setIsNewPasswordDisabled(!isValid); // Disable new password field if current password is invalid
    } catch (error) {
      console.error("Error validating current password:", error);
      setIsCurrentPasswordValid(false);
      setIsNewPasswordDisabled(true); // Disable new password field on error
    }
  };
  // Reset validation state on change
  const handleCurrentPasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);
    setAllFieldsValid(true);

    if (value.trim() === "") {
      // Disable new password fields if current password is empty
      setIsNewPasswordDisabled(false);
      setIsCurrentPasswordValid(null);
      setNewPasswordError("");
    } else {
      // Re-enable new password fields if the user starts typing again
      setIsNewPasswordDisabled(false);
      setIsCurrentPasswordValid(null); // Reset current password validation status
    }
  };
  /**
   * Validate the entered current password, when clicking outside the input field
   */
  const handleCurrentPasswordBlur = () => {
    validateCurrentPassword();
  };

  /**
   * Validate the entered passwords, and if valid - update the new password in the database.
   * TODO REPLACE ALERTS with on-page feedback
   */
  const handleSubmitChangePassword = async () => {
    // Check if the new passwords match before sending the request
    if (newPassword !== confirmNewPassword) {
      setIsPasswordMatching(false);
      return;
    }

    if (!isCurrentPasswordValid) {
      setAllFieldsValid(false);
      return;
    }

    try {
      const res = await axios.put(
        `${apiUrl}/auth/change_password/${currUsername}`,
        {
          oldPassword: currentPassword,
          newPassword,
        },
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        setSuccessfullPasswordChange(true);
      } else {
        alert("Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      alert("An error occurred while changing password");
    }
  };
  //function that will be triggered when the user blurs (unfocuses) the new password input field.
  // This function will validate the password and set the error message if it doesn't match the policy or equal to the current password.
  const handleNewPasswordBlur = () => {
    const error = validatePassword(newPassword);
    setNewPasswordError(error);
    if (error) {
      setIsConfirmationDisabled(true);
    }

    // Check if the new password is the same as the current password
    if (newPassword === currentPassword) {
      setIsNewPasswordSameAsOld(true);
      setNewPasswordError(
        "The new password must be different from the current password."
      );
    } else {
      setIsNewPasswordSameAsOld(false);
    }
  };
  return (
    <div className={styles.mainGroup}>
      {successfullPasswordChange ? (
        <p className={styles.successMsg}>Password changed successfully</p>
      ) : (
        <>
          <h2 className={styles.h2}>Change Password</h2>
          <div className={styles.formGroup}>
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
              onBlur={handleCurrentPasswordBlur}
            />
            <div className={styles.passFeedback}>
              {currentPassword && isCurrentPasswordValid === false && (
                <p className={styles.errorText}>
                  The current password is incorrect
                </p>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setIsPasswordMatching(true); // Reset error state when user types
                setNewPasswordError("");
                setIsNewPasswordSameAsOld(false);
                setIsConfirmationDisabled(false);
              }}
              onBlur={handleNewPasswordBlur}
              disabled={isNewPasswordDisabled}
            />
            {newPasswordError && (
              <p className={styles.errorText}>{newPasswordError}</p>
            )}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="confirmNewPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmNewPassword"
              value={confirmNewPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
                setIsPasswordMatching(true); // Reset error state when user types
              }}
              disabled={
                isNewPasswordDisabled ||
                isNewPasswordSameAsOld ||
                isConfirmationDisabled
              }
            />
            <div className={styles.passFeedback}>
              {!isPasswordMatching && (
                <p className={styles.errorText}>
                  The new passwords do not match
                </p>
              )}
              {!allFieldsValid && (
                <p className={styles.errorText}>
                  All fields must be valid before submitting
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleSubmitChangePassword}
            className={styles.button}
            disabled={isCurrentPasswordValid === false}
          >
            Change Password
          </button>
        </>
      )}
    </div>
  );
};

export default SettingsChangePassword;
